import React, { useState, useEffect } from "react";
import { useToast } from "../../context/ToastContext";
import { FaUser, FaKey, FaEye, FaEyeSlash, FaCoins, FaShoppingBag, FaInfoCircle, FaSignOutAlt, FaMapMarkerAlt, FaUniversity } from "react-icons/fa";
import AddressComponent from "../../components/user/AddressComponent.js";
import CouponComponent from "../../components/user/CouponComponent.js";
import OrderDetails from "../../components/user/OrderDetails.js";
import EFT from "../../components/user/EFT.js";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./User.css";
import {
  useGetUserQuery,
  useUpdateUserMutation,
  useChangePasswordMutation,
} from "../../services/userApi";
import { useDispatch } from "react-redux";
import { userApi } from "../../services/userApi";
import Loader from "../../components/Loader.js";

const User = () => {
  const { data: userData, isLoading: userLoading, refetch } = useGetUserQuery();
  const [updateUser, { isLoading: updateLoading }] = useUpdateUserMutation();
  const [changeUserPassword, { isLoading: changePasswordLoading }] = useChangePasswordMutation();
  const [searchParams] = useSearchParams();

  const { showToast } = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    ad: "",
    soyad: "",
    email: "",
    telefon: "",
    sifre: "",
    adres: "",
  });

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'settings');
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [activeForm, setActiveForm] = useState('profile');
  const [previousFormData, setPreviousFormData] = useState({
    ad: '',
    soyad: '',
    email: '',
    telefon: ''
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Kullanıcı verilerini formData'ya yükle
  useEffect(() => {
    if (userData) {
      setFormData({
        ad: userData.ad || '',
        soyad: userData.soyad || '',
        email: userData.email || '',
        telefon: userData.telefon || '',
        sifre: '',
        adres: userData.adres || '',
      });

      setPreviousFormData({
        ad: userData.ad || '',
        soyad: userData.soyad || '',
        email: userData.email || '',
        telefon: userData.telefon || ''
      });
    }
  }, [userData]);

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const toggleOldPasswordVisibility = () => {
    setShowOldPassword(!showOldPassword);
  };
  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    // Şifre doğrulama kontrolleri
    if (passwordData.newPassword.length < 6) {
      showToast('Yeni şifre en az 6 karakter olmalıdır!', 'error');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showToast('Yeni şifreler birbiriyle eşleşmiyor!', 'error');
      return;
    }

    if (passwordData.oldPassword === passwordData.newPassword) {
      showToast('Yeni şifre eski şifre ile aynı olamaz!', 'error');
      return;
    }

    try {
      const result = await changeUserPassword({
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
        userData: formData
      }).unwrap();

      showToast('Şifreniz başarıyla güncellendi!', 'success');
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setActiveForm('profile');

      // Şifre değiştikten sonra çıkış yap
      setTimeout(() => {
        logout();
      }, 1000);
    } catch (error) {
      if (error?.status === 400) {
        showToast('Mevcut şifreniz hatalı!', 'error');
      } else {
        showToast('Şifre güncellenirken bir hata oluştu!', 'error');
      }
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Telefon numarası formatlama fonksiyonu
  const formatPhoneNumber = (value) => {
    // Sadece rakamları al
    let numbers = value.replace(/\D/g, '');

    // Başında 0 yoksa ekle
    if (!numbers.startsWith('0') && numbers.length > 0) {
      numbers = '0' + numbers;
    }

    // Maksimum 11 rakam olacak şekilde kes
    numbers = numbers.slice(0, 11);

    return numbers;
  };

  // Input değişiklik handler'ı
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'telefon') {
      const formattedValue = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const changes = {
      ad: formData.ad !== previousFormData.ad,
      soyad: formData.soyad !== previousFormData.soyad,
      email: formData.email !== previousFormData.email,
      telefon: formData.telefon !== previousFormData.telefon
    };

    if (!Object.values(changes).some(changed => changed)) {
      showToast('Değişiklik yapmadınız!', 'warning');
      return;
    }

    // Ad kontrolü
    if (changes.ad && formData.ad.trim().length < 2) {
      showToast('Ad en az 2 karakter olmalıdır!', 'error');
      return;
    }

    // Soyad kontrolü
    if (changes.soyad && formData.soyad.trim().length < 2) {
      showToast('Soyad en az 2 karakter olmalıdır!', 'error');
      return;
    }

    // Email formatı kontrolü
    if (changes.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        showToast('Geçerli bir email adresi giriniz!', 'error');
        return;
      }
    }

    // Telefon numarası kontrolü
    if (changes.telefon) {
      const phoneNumbers = formData.telefon;
      if (phoneNumbers.length !== 11) {
        showToast('Telefon numarası 11 haneli olmalıdır!', 'error');
        return;
      }
    }

    try {
      await updateUser(formData).unwrap();
      showToast('Bilgiler başarıyla güncellendi!', 'success');

      // Değişiklikleri kaydettikten sonra önceki değerleri güncelle
      setPreviousFormData({
        ad: formData.ad,
        soyad: formData.soyad,
        email: formData.email,
        telefon: formData.telefon
      });

      // Şifre değiştiyse çıkış yap
      if (formData.sifre && formData.sifre.trim() !== '') {
        setTimeout(() => {
          logout();
        }, 1000);
      }
    } catch (error) {
      console.error("Form gönderme hatası:", error);
      if (error?.status === 400) {
        showToast('Güncelleme hatası: Adres bilgisi eksik veya hatalı. Lütfen adres ekleyiniz.', 'error');
      } else {
        showToast('Bilgiler güncellenemedi!', 'error');
      }
    }
  };

  const logout = () => {
    // RTK Query önbelleğini temizle
    dispatch(userApi.util.resetApiState());

    // Token'ları kaldır ve login sayfasına yönlendir
    localStorage.removeItem('token');
    // localStorage.removeItem('tkn');
    showToast('Çıkış yapıldı!', 'success');

    navigate("/login");
  };

  if (userLoading) {
    return (
      <div className="user-loading-container">
        <div className="spinner-container">
          <Loader size={40} />
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-container">
      {/* Profil başlık kısmı */}
      <div className="profile-header">
        <div className="profile-info">
          <div className="profile-avatar">
            <FaUser size={28} />
          </div>
          <div>
            <h3>Merhaba, {formData.ad} {formData.soyad}</h3>
            <p className="profile-subtitle">Hesap bilgilerinizi ve sipariş geçmişinizi bu sayfadan yönetebilirsiniz</p>
          </div>
        </div>
      </div>

      <div className="profile-content">
        {/* Sol kenar çubuğu - Sekmeler */}
        <div className="sidebar-wrapper">
          <div className="user-mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <span>&#10005;</span> : <span>&#9776;</span>}
            <span>Menü</span>
          </div>

          <div className={`sidebar-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <button
              className={`sidebar-item ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => handleTabClick('settings')}
            >
              <FaUser className="sidebar-icon" /> Hesap Bilgilerim
            </button>
            <button
              className={`sidebar-item ${activeTab === 'addresses' ? 'active' : ''}`}
              onClick={() => handleTabClick('addresses')}
            >
              <FaMapMarkerAlt className="sidebar-icon" /> Kayıtlı Adreslerim
            </button>
            <button
              className={`sidebar-item ${activeTab === 'my-earnings' ? 'active' : ''}`}
              onClick={() => handleTabClick('my-earnings')}
            >
              <FaCoins className="sidebar-icon" /> Kazançlarım
            </button>
            <button
              className={`sidebar-item ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => handleTabClick('orders')}
            >
              <FaShoppingBag className="sidebar-icon" /> Siparişlerim
            </button>
            {userData?.evden === 1 && (
              <button
                className={`sidebar-item ${activeTab === 'eft' ? 'active' : ''}`}
                onClick={() => handleTabClick('eft')}
              >
                <FaUniversity className="sidebar-icon" /> EFT Siparişleri
              </button>
            )}
            <button
              className="sidebar-item logout-button"
              onClick={logout}
            >
              <FaSignOutAlt className="sidebar-icon" /> Çıkış Yap
            </button>
          </div>
        </div>

        {/* Ana içerik alanı */}
        <div className="content-area">
          <div className="content-card">
            {activeTab === 'settings' && (
              <div className="settings-content">
                <ul className="tab-menu">
                  <li>
                    <button
                      className={`tab-button ${activeForm === 'profile' ? 'active' : ''}`}
                      onClick={() => setActiveForm('profile')}
                    >
                      <FaUser className="tab-icon" /> Profil Bilgileri
                    </button>
                  </li>
                  <li>
                    <button
                      className={`tab-button ${activeForm === 'password' ? 'active' : ''}`}
                      onClick={() => setActiveForm('password')}
                    >
                      <FaKey className="tab-icon" /> Şifre Değiştir
                    </button>
                  </li>
                </ul>

                {activeForm === 'profile' && (
                  <div className="form-container">
                    <h4 className="section-title">Hesap Bilgilerim</h4>
                    <form onSubmit={handleFormSubmit} className="form-grid">
                      <div className="form-row">
                        <div className="form-group me-0">
                          <label htmlFor="ad">Ad</label>
                          <input
                            type="text"
                            className="form-input"
                            id="ad"
                            name="ad"
                            value={formData.ad}
                            onChange={handleInputChange}
                            placeholder="Adınız"
                            required
                          />
                        </div>
                        <div className="form-group me-0">
                          <label htmlFor="soyad">Soyad</label>
                          <input
                            type="text"
                            className="form-input"
                            id="soyad"
                            name="soyad"
                            value={formData.soyad}
                            onChange={handleInputChange}
                            placeholder="Soyadınız"
                            required
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group me-0">
                          <label htmlFor="email">E-posta</label>
                          <input
                            type="email"
                            className="form-input"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="E-posta adresiniz"
                            required
                          />
                        </div>
                        <div className="form-group me-0">
                          <label htmlFor="telefon">Telefon</label>
                          <input
                            type="tel"
                            className="form-input"
                            id="telefon"
                            name="telefon"
                            value={formData.telefon}
                            onChange={handleInputChange}
                            placeholder="Telefon numaranız"
                            maxLength={17}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-button-container">
                        <button type="submit" className="update-button">
                          Bilgilerimi Güncelle
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {activeForm === 'password' && (
                  <div className="form-container">
                    <h4 className="section-title">Şifre Değiştir</h4>
                    <form onSubmit={handlePasswordSubmit} className="form-grid">
                      <div className="form-group password-group me-0">
                        <label htmlFor="oldPassword">Mevcut Şifre</label>
                        <div className="password-input-container">
                          <input
                            type={showOldPassword ? "text" : "password"}
                            className="form-input"
                            id="oldPassword"
                            name="oldPassword"
                            value={passwordData.oldPassword}
                            onChange={handlePasswordChange}
                            required
                          />
                          <button
                            className="password-toggle"
                            type="button"
                            onClick={toggleOldPasswordVisibility}
                          >
                            {showOldPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                      </div>

                      <div className="form-group password-group me-0">
                        <label htmlFor="newPassword">Yeni Şifre</label>
                        <div className="password-input-container">
                          <input
                            type={showNewPassword ? "text" : "password"}
                            className="form-input"
                            id="newPassword"
                            name="newPassword"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            required
                          />
                          <button
                            className="password-toggle"
                            type="button"
                            onClick={toggleNewPasswordVisibility}
                          >
                            {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                        <div className="form-hint">
                          <FaInfoCircle className="hint-icon" /> Şifreniz en az 6 karakter olmalıdır.
                        </div>
                      </div>

                      <div className="form-group password-group me-0">
                        <label htmlFor="confirmPassword">Yeni Şifre Tekrar</label>
                        <div className="password-input-container">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            className="form-input"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange}
                            required
                          />
                          <button
                            className="password-toggle"
                            type="button"
                            onClick={toggleConfirmPasswordVisibility}
                          >
                            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                      </div>

                      <div className="form-button-container">
                        <button
                          type="submit"
                          className="update-button w-100"
                          disabled={changePasswordLoading}
                        >
                          {changePasswordLoading ? (
                            <>
                              <span className="spinner-button"></span>
                              Güncelleniyor...
                            </>
                          ) : (
                            'Şifremi Güncelle'
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'my-earnings' && (
              <div className="earnings-wrapper">
                <h4 className="section-title">Kazançlarım</h4>
                <CouponComponent />

                
              </div>
            )}

            {activeTab === 'eft' && userData?.evden === 1 && (
              <div className="earnings-wrapper">
                <h4 className="section-title">EFT Siparişleri</h4>
                <EFT />
              </div>
            )}

            {activeTab === 'addresses' && (
              <div className="addresses-wrapper">
                <h4 className="section-title">Adreslerim</h4>
                <AddressComponent />
              </div>
            )}

            {activeTab === "orders" && (
              <div className="orders-wrapper">
                <h4 className="section-title">Siparişlerim</h4>
                <OrderDetails />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default User;